import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Link, useNavigate } from "react-router-dom";
import api from "../Api";
import Popup from "../components/PopUP";

export default function AddAirport() {
  const navigate = useNavigate();

  // Exact API payload structure based on your cURL
  const initialFormData = {
    general_information: { name: "", type: "", url: "" },
    location: { lat: "", lon: "", city: "", state: "", country: "" },
    time_information: { tz: "", utc: "" },
    codes: { code: "", icao: "" },
    runway_information: {
      number_Runways: "",
      runway_s_direction: "",
      runway_s_length_m: "",
      elevation_ft: "",
      runway_surface_type: "",
      number__terminals: "", // Double underscore exactly as API
    },
    operational_statistics: {
      annual_movements_approx: "",
      annual_passenger_traffic_approx: "",
    },
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  // Generic handler for nested state updates
  const handleNestedChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.general_information.name.trim()) newErrors.name = "Airport name is required";
    if (!formData.general_information.type.trim()) newErrors.type = "Airport type is required";
    if (!formData.location.city.trim()) newErrors.city = "City is required";
    if (!formData.location.country.trim()) newErrors.country = "Country is required";
    if (!formData.codes.code.trim()) newErrors.code = "IATA Code is required";
    if (!formData.codes.icao.trim()) newErrors.icao = "ICAO Code is required";

    // --- LATITUDE VALIDATION ---
    if (formData.location.lat === "") {
      newErrors.lat = "Latitude is required";
    } else {
      const latNum = parseFloat(formData.location.lat);
      if (isNaN(latNum) || latNum < -90 || latNum > 90) {
        newErrors.lat = "Latitude must be between -90 and 90";
      }
    }

    // --- LONGITUDE VALIDATION ---
    if (formData.location.lon === "") {
      newErrors.lon = "Longitude is required";
    } else {
      const lonNum = parseFloat(formData.location.lon);
      if (isNaN(lonNum) || lonNum < -180 || lonNum > 180) {
        newErrors.lon = "Longitude must be between -180 and 180";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const response = await api.post('/airplane-service/airport/new', formData);
        
        showToast(response.data.detail || "Airport created successfully", 'success');
        
        setFormData(initialFormData);
    
      } catch (error) {
        console.error("API Error:", error);
        const errorMsg = error.response?.data?.detail || "Failed to create airport. Please try again.";
        showToast(errorMsg, 'danger');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      showToast('Please fill all required fields correctly.', 'danger');
    }
  };

  return (
    <div className="user-management-page">
      <Sidebar />
      <div className="main-wrapper">
        <Header />

        <Popup
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
          duration={3000}
        />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  
                  {/* Top Banner / Breadcrumb */}
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <nav className="breadcrumb_nav">
                          <ul className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                              <Link to="/dashboard">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item">
                              <Link to="/airport">Airport Management</Link>
                            </li>
                            <li className="breadcrumb-item text-capitalize">
                              Add New Airport
                            </li>
                          </ul>
                        </nav>
                        <h1>Airport Management</h1>
                      </div>
                      <div className="d-flex flex-end">
                        <Link to="/airport" className="secondary_btn">
                          Back
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Form Start */}
                  <form onSubmit={handleSubmit}>
                    <div className="common-outer-card mt-4">
                      <div className="common_sub_heading_wrapper">
                        <h2>Add New Airport</h2>
                      </div>
                      
                      <div className="common_content_blk">
                        
                        {/* 1. GENERAL INFORMATION */}
                        <h5 className="mb-3">General Information</h5>
                        <div className="row g-4 mb-4">
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Airport Name <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Choiseul Bay Airport"
                                  value={formData.general_information.name}
                                  onChange={(e) => handleNestedChange("general_information", "name", e.target.value)}
                                />
                                <span className="error">{errors.name}</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Airport Type <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Civil Airport"
                                  value={formData.general_information.type}
                                  onChange={(e) => handleNestedChange("general_information", "type", e.target.value)}
                                />
                                <span className="error">{errors.type}</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Website URL</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Website URL"
                                  value={formData.general_information.url}
                                  onChange={(e) => handleNestedChange("general_information", "url", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 2. LOCATION DETAILS */}
                        <h5 className="mb-3">Location Details</h5>
                        <div className="row g-4 mb-4">
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>City <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="City"
                                  value={formData.location.city}
                                  onChange={(e) => handleNestedChange("location", "city", e.target.value)}
                                />
                                <span className="error">{errors.city}</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>State / Province</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="State"
                                  value={formData.location.state}
                                  onChange={(e) => handleNestedChange("location", "state", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Country <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Country"
                                  value={formData.location.country}
                                  onChange={(e) => handleNestedChange("location", "country", e.target.value)}
                                />
                                <span className="error">{errors.country}</span>
                              </div>
                            </div>
                          </div>
                                               {/* Latitude */}
                          <div className="col-12 col-md-6">
                            <div className="form_field">
                              <label>Latitude (deg) <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="number"
                                  step="any"
                                  min="-90"
                                  max="90"
                                  className="form-control"
                                  placeholder="e.g. -6.711944"
                                  value={formData.location.lat}
                                  onChange={(e) => handleNestedChange("location", "lat", e.target.value)}
                                />
                                <span className="error text-danger" style={{ fontSize: '12px' }}>
                                  {errors.lat}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Longitude */}
                          <div className="col-12 col-md-6">
                            <div className="form_field">
                              <label>Longitude (deg) <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="number"
                                  step="any"
                                  min="-180"
                                  max="180"
                                  className="form-control"
                                  placeholder="e.g. 156.396111"
                                  value={formData.location.lon}
                                  onChange={(e) => handleNestedChange("location", "lon", e.target.value)}
                                />
                                <span className="error text-danger" style={{ fontSize: '12px' }}>
                                  {errors.lon}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 3. CODES & TIME */}
                        <h5 className="mb-3">Codes & Time Information</h5>
                        <div className="row g-4 mb-4">
                          <div className="col-12 col-md-3">
                            <div className="form_field">
                              <label>IATA Code <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. CHY"
                                  value={formData.codes.code}
                                  onChange={(e) => handleNestedChange("codes", "code", e.target.value)}
                                />
                                <span className="error">{errors.code}</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-3">
                            <div className="form_field">
                              <label>ICAO Code <span className="text-danger">*</span></label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. AGGC"
                                  value={formData.codes.icao}
                                  onChange={(e) => handleNestedChange("codes", "icao", e.target.value)}
                                />
                                <span className="error">{errors.icao}</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-3">
                            <div className="form_field">
                              <label>Timezone (TZ)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Pacific/Guadalcanal"
                                  value={formData.time_information.tz}
                                  onChange={(e) => handleNestedChange("time_information", "tz", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-3">
                            <div className="form_field">
                              <label>UTC Offset</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. +11"
                                  value={formData.time_information.utc}
                                  onChange={(e) => handleNestedChange("time_information", "utc", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 4. RUNWAY INFORMATION */}
                        <h5 className="mb-3">Runway Information</h5>
                        <div className="row g-4 mb-4">
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Number of Runways</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. 1"
                                  value={formData.runway_information.number_Runways}
                                  onChange={(e) => handleNestedChange("runway_information", "number_Runways", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Runway Direction</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. 13/31"
                                  value={formData.runway_information.runway_s_direction}
                                  onChange={(e) => handleNestedChange("runway_information", "runway_s_direction", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Runway Length (m)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. 660.0"
                                  value={formData.runway_information.runway_s_length_m}
                                  onChange={(e) => handleNestedChange("runway_information", "runway_s_length_m", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Elevation (ft)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Not Specified"
                                  value={formData.runway_information.elevation_ft}
                                  onChange={(e) => handleNestedChange("runway_information", "elevation_ft", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Runway Surface Type</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Grass, Concrete"
                                  value={formData.runway_information.runway_surface_type}
                                  onChange={(e) => handleNestedChange("runway_information", "runway_surface_type", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="form_field">
                              <label>Number of Terminals</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Not Specified"
                                  value={formData.runway_information.number__terminals}
                                  onChange={(e) => handleNestedChange("runway_information", "number__terminals", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 5. OPERATIONAL STATISTICS */}
                        <h5 className="mb-3">Operational Statistics</h5>
                        <div className="row g-4 mb-4">
                          <div className="col-12 col-md-6">
                            <div className="form_field">
                              <label>Annual Movements (Approx)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Not Specified"
                                  value={formData.operational_statistics.annual_movements_approx}
                                  onChange={(e) => handleNestedChange("operational_statistics", "annual_movements_approx", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form_field">
                              <label>Annual Passenger Traffic (Approx)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. Not Specified"
                                  value={formData.operational_statistics.annual_passenger_traffic_approx}
                                  onChange={(e) => handleNestedChange("operational_statistics", "annual_passenger_traffic_approx", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Form Actions */}
                        <div className="main_action_blk hstack gap-3 justify-content-start mt-4">
                          <button 
                            type="button" 
                            className="bg_primary_outline_btn rounded-pill"
                            onClick={() => navigate('/airport')}
                          >
                            Cancel
                          </button>
                          <button 
                            type="submit" 
                            className="primary-btn rounded-pill"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Saving..." : "Save Airport"}
                          </button>
                        </div>

                      </div>
                    </div>
                  </form>
                  {/* Form End */}

                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}